import {
  ChangeEvent,
  createContext,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export function useDependentState<T>(value: T) {
  let [state, setState] = useState(value);
  useEffect(() => {
    setState(value);
  }, [value]);
  return [state, setState] as const;
}

type GlobalContextType = Record<
  string,
  {
    state: any;
    setStates: Set<(state: any) => void>;
  }
>;

let GlobalStateContext = createContext<GlobalContextType | null>(null);

export function GlobalStateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  let value: GlobalContextType = useMemo(() => ({}), []);

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
}

export function useGlobalState<T>(key: string, initial: T | (() => T)) {
  let context = useContext(GlobalStateContext);
  if (!context)
    throw new Error("useGlobalState must be used within a GlobalStateProvider");

  let [state, setState] = useState<T>(context[key]?.state ?? initial);
  if (!context[key]) {
    context[key] = {
      state,
      setStates: new Set(),
    };
  }
  useEffect(() => {
    context![key].setStates.add(setState);
    return () => {
      context![key].setStates.delete(setState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let returnSetState = useCallback((stateAction: SetStateAction<T>) => {
    let newState =
      typeof stateAction === "function"
        ? // @ts-ignore
          stateAction(context![key].state)
        : stateAction;
    context![key].state = newState;
    for (let set of context![key].setStates) {
      set(newState);
    }
    return newState;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [state, returnSetState] as const;
}

export function useLocalStorageState<T>(key: string, initial: T) {
  let [state, setState] = useGlobalState(key, initial);
  useEffect(() => {
    let stored = localStorage.getItem(key);
    if (stored) {
      setState(JSON.parse(stored));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  let returnSetState = useCallback((stateAction: SetStateAction<T>) => {
    let newState = setState(stateAction);
    localStorage.setItem(key, JSON.stringify(newState));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [state, returnSetState] as const;
}

export function onChangeSet(setState: (value: string) => void) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setState(event.target.value);
  };
}

export function useEventListener(
  object: EventTarget,
  type: string,
  listener: EventListenerOrEventListenerObject,
) {
  useEffect(() => {
    object.addEventListener(type, listener);
    return () => {
      object.removeEventListener(type, listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
